// useAvatarUpload — wire a chosen File through validation, optional
// in-browser compression, and a Supabase Storage upload to the public
// `avatars` bucket. Returns the helpers the Settings panel uses.
//
// The avatar path convention (matches API.md §4.4) is
//   avatars/<userId>/avatar-<timestamp>-<rand>.webp
//
// Why a per-user folder + per-upload suffix instead of a single
//   avatars/<userId>.webp
// path:
//   * The RLS policy on storage.objects only permits INSERT, not
//     UPDATE or DELETE (migration 014 — `avatars self write` policy,
//     no delete/update policy exists). So overwriting in place needs
//     a delete policy we don't currently have.
//   * Using a per-upload suffix avoids that and gives us stable
//     URL-only "history" (old avatar objects accumulate harmlessly
//     at the bucket level; the row's avatar_url always points at the
//     most recent one). The image bytes are tiny and never accessed
//     again once the URL is replaced.
//   * We optimistically clear the row's avatar_url after the upload
//     settles, so the previous file is unreachable from the app.
//
// We do compress to webp in the browser so the bytes that hit Storage
// are small (≤ ~200 KiB at 512×512), which keeps the 5 MiB bucket
// cap from being a UX block on big phone photos.

import { useCallback, useState } from "react";
import { supabase, isAuthConfigured } from "./supabase";
import {
  MAX_IMAGE_BYTES,
  prepareImage,
  randSuffix,
  friendlyUploadErr,
} from "./imageUpload";

const BUCKET = "avatars";

export type AvatarUploadPhase =
  | "idle"
  | "compressing"
  | "uploading"
  | "saving"
  | "done"
  | "error";

export type AvatarUploadState =
  | { phase: "idle" }
  | { phase: "compressing" }
  | { phase: "uploading" }
  | { phase: "saving" }
  | { phase: "done"; url: string }
  | { phase: "error"; message: string };

export type AvatarUploadApi = {
  state: AvatarUploadState;
  /** Replace the user's avatar. On success the new public URL is
   *  written to profiles.avatar_url in the same flow; the caller
   *  then calls refreshProfile() so the change is visible. */
  upload: (
    userId: string,
    file: File,
  ) => Promise<{ url: string } | null>;
  /** Clear the user's avatar. Returns true when profiles.avatar_url
   *  has been set to null; UI can then refresh to show initials. */
  remove: (userId: string) => Promise<boolean>;
  reset: () => void;
};

export function useAvatarUpload(): AvatarUploadApi {
  const [state, setState] = useState<AvatarUploadState>({ phase: "idle" });

  const reset = useCallback(() => {
    setState({ phase: "idle" });
  }, []);

  const upload = useCallback(
    async (userId: string, file: File): Promise<{ url: string } | null> => {
      if (!isAuthConfigured()) {
        setState({ phase: "error", message: "尚未設定 Supabase 環境變數。" });
        return null;
      }
      if (!userId) {
        setState({ phase: "error", message: "未登入，無法上傳頭像。" });
        return null;
      }
      if (!file.type.startsWith("image/")) {
        setState({ phase: "error", message: "請選擇圖片檔案（PNG / JPEG / WebP）。" });
        return null;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        setState({
          phase: "error",
          message: `圖片太大（${(file.size / 1024 / 1024).toFixed(1)} MiB），上限為 5 MiB。`,
        });
        return null;
      }

      // 1. Compress / re-encode to webp in the browser so the Storage
      //    upload is small even when the user picks a 4 MiB phone
      //    photo. Browsers without canvas support (very rare) fall
      //    back to the original file.
      setState({ phase: "compressing" });
      const prepared = await prepareImage(file);

      // 2. Upload to a deterministic-but-unique path under the user's
      //    folder. The `avatars/<userId>/...` prefix is the only
      //    thing the `avatars self write` RLS policy looks at; the
      //    `auth.uid()` check inside the policy is what authorises
      //    the insert.
      setState({ phase: "uploading" });
      const objectPath = `${userId}/avatar-${Date.now()}-${randSuffix()}.webp`;

      const { error: uploadErr } = await supabase.storage
        .from(BUCKET)
        .upload(objectPath, prepared, {
          contentType: "image/webp",
          upsert: false,
          cacheControl: "3600",
        });

      if (uploadErr) {
        setState({ phase: "error", message: friendlyUploadErr(uploadErr.message, { asset: "頭像" }) });
        return null;
      }

      // 3. Resolve the public URL (bucket is public per migration 013).
      const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(objectPath);
      const url = pub.publicUrl;
      if (!url) {
        setState({ phase: "error", message: "上傳成功但無法取得公開連結。" });
        return null;
      }

      // 4. Persist the URL on the profile row. RLS profiles_self_rw
      //    (migration 014) limits the update to id = auth.uid().
      setState({ phase: "saving" });
      const { error: patchErr } = await supabase
        .from("profiles")
        .update({ avatar_url: url })
        .eq("id", userId);
      if (patchErr) {
        setState({ phase: "error", message: "已上傳但寫入個人檔案失敗：" + patchErr.message });
        return null;
      }

      setState({ phase: "done", url });
      return { url };
    },
    [],
  );

  const remove = useCallback(
    async (userId: string): Promise<boolean> => {
      if (!isAuthConfigured()) {
        setState({ phase: "error", message: "尚未設定 Supabase 環境變數。" });
        return false;
      }
      if (!userId) return false;
      setState({ phase: "saving" });
      const { error } = await supabase
        .from("profiles")
        .update({ avatar_url: null })
        .eq("id", userId);
      if (error) {
        setState({ phase: "error", message: "無法移除頭像：" + error.message });
        return false;
      }
      setState({ phase: "idle" });
      return true;
    },
    [],
  );

  return { state, upload, remove, reset };
}
